import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronRight, Plus } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HomeGamesSection } from '@/components/features/home/HomeGamesSection';
import { HomePlayerGroupsSection } from '@/components/features/home/HomePlayerGroupsSection';
import { HomePlayersSection } from '@/components/features/home/HomePlayersSection';
import { HomeStatsSection } from '@/components/features/home/HomeStatsSection';
import { HomeTeamsSection } from '@/components/features/home/HomeTeamsSection';
import { HomeTemplatesSection } from '@/components/features/home/HomeTemplatesSection';
import { EmptySessionsState } from '@/components/features/sessions/EmptySessionsState';
import { SessionCardSkeleton } from '@/components/features/sessions/SessionCardSkeleton';
import { SessionCard } from '@/components/shared/cards';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useGameDefinitions } from '@/hooks/useGameDefinitions';
import { useGlobalAnalytics } from '@/hooks/useGlobalAnalytics';
import { useHomeStats } from '@/hooks/useHomeStats';
import { useInitialSync } from '@/hooks/useInitialSync';
import { usePlayerGroups } from '@/hooks/usePlayerGroups';
import { usePlayers } from '@/hooks/usePlayers';
import { useRouteAnimation } from '@/hooks/useRouteAnimation';
import { useSessions } from '@/hooks/useSessions';
import { useSessionTemplates } from '@/hooks/useSessionTemplates';
import { useTeamPresets } from '@/hooks/useTeamPresets';
import { fadeIn, transitions } from '@/lib/motion-config';
import type { GameDefinition } from '@/schemas';

export const Route = createFileRoute('/')({
  component: IndexPage,
});

function IndexPage() {
  const { t } = useTranslation('common');
  const { isSyncing } = useInitialSync();
  const {
    sessions,
    isInitialLoading: isSessionsLoading,
    hasData: hasSessionData,
    refresh: refreshSessions,
  } = useSessions();
  const { analytics, isLoading: isAnalyticsLoading } = useGlobalAnalytics(t);
  const { players, isLoading: isPlayersLoading } = usePlayers();
  const { playerGroups, isLoading: isGroupsLoading } = usePlayerGroups();
  const { gameDefinitions, isLoading: isGamesLoading } = useGameDefinitions();
  const { sessionTemplates, isLoading: isTemplatesLoading } = useSessionTemplates();
  const { teamPresets, isLoading: isTeamsLoading } = useTeamPresets();
  const { sessionCount, playerCount, hasCustomGame } = useHomeStats();

  useDocumentTitle(`${t('nav.home', 'Home')} - ${t('app.name', 'One Score')}`);

  // Progressive disclosure: show sections conditionally based on user progress
  const showPlayers = playerCount >= 1;
  const showGroups = playerCount >= 1;
  const showTemplates = sessionCount >= 1;
  const showGames = sessionCount >= 3 || hasCustomGame;
  const showTeams = sessionCount >= 1 && playerCount >= 2;

  // Determine loading states
  const showInitialSkeleton = (isSyncing || isSessionsLoading) && !hasSessionData;

  // Active sessions (ongoing)
  const activeSessions = useMemo(() => {
    return sessions.filter((s) => s.status === 'ongoing' && !s.archivedAt && !s.deletedAt);
  }, [sessions]);

  // Recent finished sessions (history) - always show last 3-5
  const recentFinishedSessions = useMemo(() => {
    return sessions
      .filter((s) => s.status === 'finished' && !s.archivedAt && !s.deletedAt)
      .toSorted((a, b) => (b.endedAt ?? 0) - (a.endedAt ?? 0))
      .slice(0, 5);
  }, [sessions]);

  const totalSessionsCount = useMemo(() => {
    return sessions.filter((s) => !s.archivedAt && !s.deletedAt).length;
  }, [sessions]);

  // Determine if sections are empty (when visible and not loading)
  const playersCount = players.filter((p) => !p.deletedAt).length;
  const isPlayersEmpty =
    showPlayers && !isPlayersLoading && !isAnalyticsLoading && !isSyncing && playersCount === 0;
  const isGroupsEmpty = showGroups && !isGroupsLoading && !isSyncing && playerGroups.length === 0;
  const customGames = useMemo(() => {
    return gameDefinitions.filter(
      (g) => !(g as GameDefinition & { isSystemPreset?: boolean }).isSystemPreset,
    );
  }, [gameDefinitions]);
  const isGamesEmpty = showGames && !isGamesLoading && !isSyncing && customGames.length === 0;
  const isTeamsEmpty = showTeams && !isTeamsLoading && !isSyncing && teamPresets.length === 0;
  const isTemplatesEmpty =
    showTemplates && !isTemplatesLoading && !isSyncing && sessionTemplates.length === 0;

  // Define sections with their order and emptiness status
  // Default order: Players, Groups, Games, Teams, Templates (Templates always last)
  const sections = useMemo(() => {
    const sectionList: Array<{
      id: string;
      isEmpty: boolean;
      order: number;
    }> = [];

    if (showPlayers) {
      sectionList.push({ id: 'players', isEmpty: isPlayersEmpty, order: 1 });
    }
    if (showGroups) {
      sectionList.push({ id: 'groups', isEmpty: isGroupsEmpty, order: 2 });
    }
    if (showGames) {
      sectionList.push({ id: 'games', isEmpty: isGamesEmpty, order: 3 });
    }
    if (showTeams) {
      sectionList.push({ id: 'teams', isEmpty: isTeamsEmpty, order: 4 });
    }
    if (showTemplates) {
      sectionList.push({ id: 'templates', isEmpty: isTemplatesEmpty, order: 5 });
    }

    // Sort: non-empty first (by order), then empty (by order), but templates always last
    return sectionList.toSorted((a, b) => {
      // Templates always last
      if (a.id === 'templates' && b.id !== 'templates') return 1;
      if (b.id === 'templates' && a.id !== 'templates') return -1;
      // If both are templates, maintain order
      if (a.id === 'templates' && b.id === 'templates') return 0;

      // Non-empty sections come before empty sections
      if (!a.isEmpty && b.isEmpty) return -1;
      if (a.isEmpty && !b.isEmpty) return 1;

      // Within same emptiness status, maintain default order
      return a.order - b.order;
    });
  }, [
    showPlayers,
    showGroups,
    showGames,
    showTeams,
    showTemplates,
    isPlayersEmpty,
    isGroupsEmpty,
    isGamesEmpty,
    isTeamsEmpty,
    isTemplatesEmpty,
  ]);

  const { shouldAnimate, routeKey } = useRouteAnimation();

  return (
    <motion.div
      key={routeKey}
      variants={fadeIn}
      initial={shouldAnimate ? 'initial' : false}
      animate="animate"
      transition={transitions.springGentle}
      className="w-full max-w-6xl space-y-8 px-4 mx-auto"
    >
      {/* Header Section */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('nav.home', 'Home')}</h1>
          <p className="text-muted-foreground">
            {t('nav.home.description', 'One Score to rule them all')}
          </p>
        </div>
        {sessions.length > 0 && (
          <Link to="/sessions/new" className="flex-1 sm:flex-none">
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              {t('sessions.newButton', 'New Session')}
            </Button>
          </Link>
        )}
      </div>

      {/* Active Sessions */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          {activeSessions.length > 0 ? (
            <Link
              to="/sessions"
              className="group flex items-baseline gap-3 transition-opacity hover:opacity-80"
              aria-label={t('home.sessions.viewAll', 'View All Sessions')}
            >
              <h2 className="text-2xl font-semibold tracking-tight">
                {t('home.sessions.activeTitle', 'Sessions')}
              </h2>
              <span className="text-sm text-muted-foreground flex items-center">
                {t('home.sessions.activeCount', {
                  count: activeSessions.length,
                  defaultValue: '{{count}} active',
                })}
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/70 ml-1 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ) : (
            <h2 className="text-2xl font-semibold tracking-tight">
              {t('home.sessions.activeTitle', 'Sessions')}
            </h2>
          )}
          {totalSessionsCount > 0 && (
            <Link to="/sessions/new">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 sm:w-auto sm:px-3 sm:gap-2"
                aria-label={t('sessions.newButton', 'New Session')}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">{t('sessions.newButton', 'New Session')}</span>
              </Button>
            </Link>
          )}
        </div>

        {/* Loading State */}
        {showInitialSkeleton && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SessionCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty State - only show if no sessions at all */}
        {!showInitialSkeleton && sessions.length === 0 && <EmptySessionsState />}

        {/* Active Sessions Grid */}
        {!showInitialSkeleton && activeSessions.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {activeSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onSessionChange={() => {
                  void refreshSessions();
                }}
              />
            ))}
          </div>
        )}

        {/* Empty state for active sessions only */}
        {!showInitialSkeleton &&
          sessions.length > 0 &&
          activeSessions.length === 0 &&
          recentFinishedSessions.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>{t('home.sessions.noActive', 'No active sessions')}</p>
            </div>
          )}
      </section>

      {/* Statistics Highlights */}
      <HomeStatsSection analytics={analytics} isLoading={isAnalyticsLoading || isSyncing} />

      {/* Session History */}
      {recentFinishedSessions.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <Link
              to="/sessions"
              className="group flex items-baseline gap-3 transition-opacity hover:opacity-80"
              aria-label={t('home.sessions.viewAll', 'View All Sessions')}
            >
              <h2 className="text-2xl font-semibold tracking-tight">
                {t('home.sessions.historyTitle', 'History')}
              </h2>
              <span className="text-sm text-muted-foreground flex items-center">
                {t('home.sessions.historyCount', {
                  count: recentFinishedSessions.length,
                  defaultValue: '{{count}} recent',
                })}
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/70 ml-1 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
            <Link to="/sessions/new">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 sm:w-auto sm:px-3 sm:gap-2"
                aria-label={t('sessions.newButton', 'New Session')}
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">{t('sessions.newButton', 'New Session')}</span>
              </Button>
            </Link>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recentFinishedSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onSessionChange={() => {
                  void refreshSessions();
                }}
              />
            ))}
          </div>
        </section>
      )}

      {/* Render sections in the computed order */}
      {sections.map((section) => {
        switch (section.id) {
          case 'players': {
            return (
              <HomePlayersSection
                key="players"
                topPlayers={analytics?.topPlayers ?? []}
                recentPlayers={players}
                totalCount={playersCount}
                isLoading={isPlayersLoading || isAnalyticsLoading || isSyncing}
              />
            );
          }
          case 'groups': {
            return (
              <HomePlayerGroupsSection
                key="groups"
                playerGroups={playerGroups}
                players={players}
                isLoading={isGroupsLoading || isSyncing}
              />
            );
          }
          case 'games': {
            return (
              <HomeGamesSection
                key="games"
                gameDefinitions={gameDefinitions}
                isLoading={isGamesLoading || isSyncing}
              />
            );
          }
          case 'teams': {
            return (
              <HomeTeamsSection
                key="teams"
                teamPresets={teamPresets}
                isLoading={isTeamsLoading || isSyncing}
              />
            );
          }
          case 'templates': {
            return (
              <HomeTemplatesSection
                key="templates"
                sessionTemplates={sessionTemplates}
                isLoading={isTemplatesLoading || isSyncing}
              />
            );
          }
          default: {
            return undefined;
          }
        }
      })}
    </motion.div>
  );
}
